import { useParams, useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { useNetworkStore } from "../stores/network";
import { useBlockDetail } from "../api/hooks";
import {
  PageContainer,
  PageTitle,
  Card,
  Badge,
  Mono,
  Loading,
  Flex,
  Button,
} from "../components/ui";
import { HexDisplay } from "../components/HexDisplay";
import { TxTable, type TxRow } from "../components/TxTable";
import { theme } from "../lib/theme";
import { media } from "../lib/breakpoints";
import { formatFJ, formatFJPerMana } from "../lib/format";

const Field = styled.div`
  margin-bottom: ${theme.spacing.sm};
`;

const FieldLabel = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 2px;
`;

const FieldValue = styled(Mono)`
  font-size: ${theme.fontSize.sm};
  word-break: break-all;
`;

const Unit = styled.span`
  color: ${theme.colors.textMuted};
  font-size: ${theme.fontSize.xs};
  margin-left: 4px;
`;

const UsdEstimate = styled.span`
  margin-left: 8px;
  opacity: 0.7;
  font-size: ${theme.fontSize.xs};
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};

  ${media.sm} {
    grid-template-columns: 1fr;
  }
`;

const SectionTitle = styled.h3`
  font-size: ${theme.fontSize.md};
  font-weight: 600;
  color: ${theme.colors.text};
  margin: ${theme.spacing.lg} 0 ${theme.spacing.sm};
`;

const ManaBar = styled.div`
  height: 8px;
  background: ${theme.colors.bg};
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
`;

const ManaFill = styled.div<{ pct: number }>`
  height: 100%;
  width: ${(p) => Math.min(p.pct, 100)}%;
  background: ${(p) =>
    p.pct > 80 ? theme.colors.danger
    : p.pct > 50 ? theme.colors.warning
    : theme.colors.primary};
  border-radius: 4px;
`;

function statusColor(status: string) {
  if (status === "finalized" || status === "proven") return theme.colors.success;
  if (status === "checkpointed" || status === "proposed") return theme.colors.accent;
  if (status === "dropped") return theme.colors.danger;
  return theme.colors.warning;
}

function formatSmallUsd(n: number): string {
  if (n === 0) return "$0";
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  if (n >= 1e-6) return `$${n.toFixed(8)}`;
  return `$${n.toExponential(2)}`;
}

export function BlockDetail() {
  const { blockNumber: blockNumStr } = useParams<{ blockNumber: string }>();
  const { selectedNetwork } = useNetworkStore();
  const navigate = useNavigate();
  const blockNumber = parseInt(blockNumStr ?? "0", 10);

  const { data, isLoading, isError } = useBlockDetail(selectedNetwork, blockNumber);

  if (isLoading) return <Loading />;

  if (isError || !data) {
    return (
      <PageContainer>
        <Card>
          <SectionTitle>Block not found</SectionTitle>
          <p style={{ color: theme.colors.textMuted }}>
            No block #{blockNumber} exists on this network.
          </p>
        </Card>
      </PageContainer>
    );
  }

  const { block, checkpoint, transactions, previousBlockTimestamp, manaUtilizationPct } = data;
  const pricing = (data as Record<string, unknown>).feePricing as {
    costUsd: number; costEth: number; ethUsdPrice: number; ethPerFeeAssetE12: string; snapshotAgeSeconds?: number;
  } | null;

  const blockTime = block.timestamp != null && previousBlockTimestamp != null
    ? block.timestamp - previousBlockTimestamp
    : null;

  const cpStatus = checkpoint?.finalizedAt
    ? "finalized"
    : checkpoint?.provenAt
      ? "proven"
      : checkpoint
        ? "checkpointed"
        : null;

  const txRows: TxRow[] = transactions.map((tx) => ({
    txHash: tx.txHash,
    status: tx.status,
    blockNumber: (tx as Record<string, unknown>).blockNumber as number ?? blockNumber,
    numNoteHashes: tx.numNoteHashes,
    numNullifiers: tx.numNullifiers,
    numL2ToL1Msgs: tx.numL2ToL1Msgs,
    numPrivateLogs: (tx as Record<string, unknown>).numPrivateLogs as number ?? 0,
    numContractClassLogs: (tx as Record<string, unknown>).numContractClassLogs as number ?? 0,
    numPublicLogs: (tx as Record<string, unknown>).numPublicLogs as number | null ?? null,
    gasLimitDa: (tx as Record<string, unknown>).gasLimitDa as number | null ?? null,
    gasLimitL2: (tx as Record<string, unknown>).gasLimitL2 as number | null ?? null,
    maxFeePerDaGas: (tx as Record<string, unknown>).maxFeePerDaGas as number | null ?? null,
    maxFeePerL2Gas: (tx as Record<string, unknown>).maxFeePerL2Gas as number | null ?? null,
    numSetupCalls: tx.numSetupCalls,
    numAppCalls: tx.numAppCalls,
    hasTeardown: (tx as Record<string, unknown>).hasTeardown as boolean ?? false,
    totalPublicCalldataSize: tx.totalPublicCalldataSize,
    expirationTimestamp: (tx as Record<string, unknown>).expirationTimestamp as number | null ?? null,
    feePayer: tx.feePayer,
  }));

  return (
    <PageContainer>
      <Flex justify="space-between" style={{ marginBottom: theme.spacing.md }}>
        <PageTitle style={{ marginBottom: 0 }}>Block #{blockNumber.toLocaleString()}</PageTitle>
        <Flex gap="8px">
          <Button variant="ghost" onClick={() => navigate(`/${selectedNetwork}/block/${blockNumber - 1}`)}>
            ← Prev
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/${selectedNetwork}/block/${blockNumber + 1}`)}>
            Next →
          </Button>
        </Flex>
      </Flex>

      {cpStatus && (
        <Badge color={statusColor(cpStatus)} style={{ marginBottom: theme.spacing.md, display: "inline-block" }}>
          {cpStatus}
        </Badge>
      )}

      <MetadataGrid>
        <Card>
          <Field>
            <FieldLabel>Block Hash</FieldLabel>
            <FieldValue>{block.blockHash ?? "—"}</FieldValue>
          </Field>
          <Flex gap="24px" wrap>
            <Field>
              <FieldLabel>Timestamp</FieldLabel>
              <FieldValue>
                {block.timestamp ? new Date(block.timestamp * 1000).toLocaleString() : "—"}
              </FieldValue>
            </Field>
            {blockTime != null && (
              <Field>
                <FieldLabel>Block Time</FieldLabel>
                <FieldValue>{blockTime}s</FieldValue>
              </Field>
            )}
            <Field>
              <FieldLabel>Slot</FieldLabel>
              <FieldValue>{block.slotNumber?.toLocaleString() ?? "—"}</FieldValue>
            </Field>
          </Flex>
          <Field>
            <FieldLabel>Sequencer</FieldLabel>
            <FieldValue><HexDisplay address={block.coinbase ?? ""} /></FieldValue>
          </Field>
          <Field>
            <FieldLabel>Transactions</FieldLabel>
            <FieldValue>{block.numTxs}</FieldValue>
          </Field>
        </Card>

        <Card>
          <Field>
            <FieldLabel>Total Fees</FieldLabel>
            <FieldValue>
              {block.totalFees ? formatFJ(block.totalFees) : "—"}
              <Unit>FJ</Unit>
              {pricing && (
                <UsdEstimate>
                  ≈ {formatSmallUsd(pricing.costUsd)}
                  {pricing.snapshotAgeSeconds != null && pricing.snapshotAgeSeconds > 10800 && (
                    <span
                      title={`Stale pricing — data is ${Math.round(pricing.snapshotAgeSeconds / 3600)}h old`}
                      style={{ marginLeft: 6, color: "#ffc66c", fontWeight: 600, cursor: "help" }}
                    >
                      ⚠
                    </span>
                  )}
                </UsdEstimate>
              )}
            </FieldValue>
          </Field>
          <Flex gap="24px" wrap>
            <Field>
              <FieldLabel>Base Fee per DA Mana</FieldLabel>
              <FieldValue>
                {block.feePerDaGas ?? "—"}<Unit>FPA/mana</Unit>
              </FieldValue>
            </Field>
            <Field>
              <FieldLabel>Base Fee per L2 Mana</FieldLabel>
              <FieldValue>
                {block.feePerL2Gas ? formatFJPerMana(block.feePerL2Gas) : "—"}<Unit>FJ/mana</Unit>
              </FieldValue>
            </Field>
          </Flex>
          <Field>
            <FieldLabel>Total Mana Used</FieldLabel>
            <FieldValue>
              {block.totalManaUsed ? Number(block.totalManaUsed).toLocaleString() : "—"}<Unit>mana</Unit>
            </FieldValue>
            {manaUtilizationPct != null && (
              <>
                <ManaBar><ManaFill pct={manaUtilizationPct} /></ManaBar>
                <span style={{ fontSize: theme.fontSize.xs, color: theme.colors.textMuted }}>
                  {manaUtilizationPct}% utilization
                </span>
              </>
            )}
          </Field>
        </Card>
      </MetadataGrid>

      {checkpoint && (
        <>
          <SectionTitle>Checkpoint #{checkpoint.checkpointNumber}</SectionTitle>
          <Card>
            <Flex gap="24px" wrap>
              <Field>
                <FieldLabel>Block Range</FieldLabel>
                <FieldValue>{checkpoint.startBlock}–{checkpoint.endBlock}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Position</FieldLabel>
                <FieldValue>Block {(block.indexWithinCheckpoint ?? 0) + 1} of {checkpoint.blockCount}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Attestations</FieldLabel>
                <FieldValue>{checkpoint.attestationCount ?? "—"}</FieldValue>
              </Field>
            </Flex>
            <Flex gap="24px" wrap>
              {checkpoint.l1BlockNumber != null && (
                <Field>
                  <FieldLabel>L1 Block</FieldLabel>
                  <FieldValue>{checkpoint.l1BlockNumber.toLocaleString()}</FieldValue>
                </Field>
              )}
              {checkpoint.provenAt && (
                <Field>
                  <FieldLabel>Proven At</FieldLabel>
                  <FieldValue>{new Date(checkpoint.provenAt).toLocaleString()}</FieldValue>
                </Field>
              )}
              {checkpoint.finalizedAt && (
                <Field>
                  <FieldLabel>Finalized At</FieldLabel>
                  <FieldValue>{new Date(checkpoint.finalizedAt).toLocaleString()}</FieldValue>
                </Field>
              )}
            </Flex>
          </Card>
        </>
      )}

      {txRows.length > 0 && (
        <>
          <SectionTitle>Transactions ({txRows.length})</SectionTitle>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <TxTable rows={txRows} showIndex />
          </Card>
        </>
      )}
    </PageContainer>
  );
}
